import { useState } from 'react';
import { TbBan, TbPencil, TbTrash } from 'react-icons/tb';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { ConfirmationSheet } from '../components/ConfirmationSheet';
import { useFocusTrap } from '../components/useFocusTrap';
import { adminApi as defaultAdminApi } from './admin-api';

const VOID_REASON_MAX = 300;

/**
 * Acciones de recepción sobre un período no anulado (spec 2026-10-08): "Editar" y, según tenga
 * pagos o no, "Anular período" (pide motivo, deja período y pagos como anulados) o "Eliminar
 * período" (lo borra). El servidor es quien valida cada regla; acá sólo se muestran sus mensajes.
 */
export function MembershipActions({ api = defaultAdminApi, membership, plans, onChanged }) {
  const { handleApiError } = useAuth();
  const [mode, setMode] = useState(null); // 'edit' | 'delete' | 'void'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);
  const [reason, setReason] = useState('');
  const sheetRef = useFocusTrap(mode === 'edit' || mode === 'void');
  const hasPayments = Array.isArray(membership.payments) && membership.payments.length > 0;
  // El plan actual se ofrece aunque esté inactivo (no cambiarlo es válido); el resto, sólo activos.
  const planOptions = (plans || []).filter((plan) => plan.isActive || plan.id === membership.planId);

  function open(nextMode) {
    setError('');
    setReason('');
    setForm({
      planId: membership.planId || '',
      startDate: membership.startDate || '',
      endDate: membership.endDate || '',
      amount: membership.amount !== undefined && membership.amount !== null ? String(membership.amount) : '',
    });
    setMode(nextMode);
  }

  function close() {
    if (!busy) setMode(null);
  }

  async function run(action, message) {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await action();
      setMode(null);
      if (onChanged) onChanged(message);
    } catch (caught) {
      const outcome = handleApiError(caught);
      if (!outcome.handled) setError(errorMessageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  function submitEdit(event) {
    event.preventDefault();
    if (!form.startDate || !form.endDate) { setError('Completá las dos fechas.'); return; }
    if (!/^\d+([.,]\d{1,2})?$/.test(form.amount.trim())) { setError('Ingresá un importe válido (por ejemplo 9000 o 9000.50).'); return; }
    // Sólo viaja lo que cambió: el servidor rechaza campos que no conoce y no toca lo demás.
    const changes = {};
    if (form.planId !== membership.planId) changes.planId = form.planId;
    if (form.startDate !== membership.startDate) changes.startDate = form.startDate;
    if (form.endDate !== membership.endDate) changes.endDate = form.endDate;
    const amount = form.amount.trim().replace(',', '.');
    if (Number(amount) !== Number(membership.amount)) changes.amount = amount;
    if (Object.keys(changes).length === 0) { setMode(null); return; }
    run(() => api.updateMembership(membership.id, changes), 'Período actualizado.');
  }

  function submitVoid(event) {
    event.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) { setError('Escribí el motivo.'); return; }
    run(() => api.voidMembership(membership.id, trimmed), 'Período anulado.');
  }

  const setField = (name) => (event) => setForm((current) => ({ ...current, [name]: event.target.value }));

  return (
    <div className="gym-period-actions">
      <button type="button" className="gym-button gym-button--secondary gym-period-actions__button" onClick={() => open('edit')}>
        <TbPencil aria-hidden="true" focusable="false" />Editar
      </button>
      {hasPayments ? (
        <button type="button" className="gym-button gym-button--danger gym-period-actions__button" onClick={() => open('void')}>
          <TbBan aria-hidden="true" focusable="false" />Anular período
        </button>
      ) : (
        <button type="button" className="gym-button gym-button--danger gym-period-actions__button" onClick={() => open('delete')}>
          <TbTrash aria-hidden="true" focusable="false" />Eliminar período
        </button>
      )}

      {mode === 'delete' && (
        <ConfirmationSheet
          title="¿Eliminar este período?"
          confirmLabel="Sí, eliminar"
          pendingLabel="Eliminando…"
          busy={busy}
          error={error}
          onConfirm={() => run(() => api.deleteMembership(membership.id), 'Período eliminado.')}
          onClose={close}
        />
      )}

      {mode === 'edit' && form && (
        <div className="gym-period-sheet-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
          <form
            ref={sheetRef}
            tabIndex={-1}
            className="gym-form gym-period-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Editar período"
            noValidate
            onSubmit={submitEdit}
            onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); close(); } }}
          >
            <h2 className="gym-period-sheet__title">Editar período</h2>
            {error && <p role="alert" className="gym-form__error">{error}</p>}
            <div className="gym-field">
              <label htmlFor={`period-plan-${membership.id}`} className="gym-field__label">Plan</label>
              <select id={`period-plan-${membership.id}`} className="gym-input" value={form.planId} disabled={busy} onChange={setField('planId')}>
                {!planOptions.some((plan) => plan.id === membership.planId) && <option value={membership.planId}>{membership.planName}</option>}
                {planOptions.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}
              </select>
            </div>
            <div className="gym-field">
              <label htmlFor={`period-start-${membership.id}`} className="gym-field__label">Fecha de inicio</label>
              <input id={`period-start-${membership.id}`} type="date" className="gym-input" required value={form.startDate} disabled={busy} onChange={setField('startDate')} />
            </div>
            <div className="gym-field">
              <label htmlFor={`period-end-${membership.id}`} className="gym-field__label">Hasta, sin incluir</label>
              <input id={`period-end-${membership.id}`} type="date" className="gym-input" required value={form.endDate} disabled={busy} onChange={setField('endDate')} />
            </div>
            <div className="gym-field">
              <label htmlFor={`period-amount-${membership.id}`} className="gym-field__label">Importe</label>
              <input id={`period-amount-${membership.id}`} type="text" inputMode="decimal" className="gym-input" required value={form.amount} disabled={busy} onChange={setField('amount')} />
              <p className="gym-admin-field-hint">No puede ser menor a lo ya cobrado en este período.</p>
            </div>
            <div className="gym-period-sheet__actions">
              <button type="submit" className="gym-button gym-button--primary" disabled={busy}>{busy ? 'Guardando…' : 'Guardar cambios'}</button>
              <button type="button" className="gym-button gym-button--secondary" disabled={busy} onClick={close}>Cancelar</button>
            </div>
          </form>
        </div>
      )}

      {mode === 'void' && (
        <div className="gym-period-sheet-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
          <form
            ref={sheetRef}
            tabIndex={-1}
            className="gym-form gym-period-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Anular período"
            noValidate
            onSubmit={submitVoid}
            onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); close(); } }}
          >
            <h2 className="gym-period-sheet__title">Anular período</h2>
            <p className="gym-period-sheet__text">El período y sus pagos van a quedar anulados y dejan de sumar en los totales. No se puede deshacer.</p>
            {error && <p role="alert" className="gym-form__error">{error}</p>}
            <div className="gym-field">
              <label htmlFor={`period-void-reason-${membership.id}`} className="gym-field__label">Motivo</label>
              <textarea id={`period-void-reason-${membership.id}`} className="gym-input" required maxLength={VOID_REASON_MAX} value={reason} disabled={busy} onChange={(event) => setReason(event.target.value)} />
              <span className="gym-period-sheet__counter">{reason.length}/{VOID_REASON_MAX}</span>
            </div>
            <div className="gym-period-sheet__actions">
              <button type="submit" className="gym-button gym-button--danger gym-period-sheet__confirm" disabled={busy || !reason.trim()}>{busy ? 'Anulando…' : 'Anular período'}</button>
              <button type="button" className="gym-button gym-button--secondary" disabled={busy} onClick={close}>Cancelar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
